import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from 'react-query';

import { createReportView, type CreateReportViewInput } from '../api/createReportView';
import { deleteReportView } from '../api/deleteReportView';
import { fetchReportViews } from '../api/fetchReportViews';
import { forkReportView, type ForkReportViewInput } from '../api/forkReportView';
import { shareReportView } from '../api/shareReportView';
import { updateReportView, type UpdateReportViewInput } from '../api/updateReportView';
import type { ReportView, ReportViewVisibility } from '../api/reportViewTypes';

type ReportViewsQueryData = {
    views: ReportView[];
};

/**
 * Keep the views list in sync immediately after create/update/fork.
 * Without this, navigating to a brand-new view id races the refetch and
 * Reports.tsx briefly treats the id as unknown and resets to the default report.
 */
function upsertReportViewInCache(
    queryClient: ReturnType<typeof useQueryClient>,
    eventId: number,
    nextView: ReportView,
) {
    queryClient.setQueryData(
        queryKeys.reports.views(eventId),
        (current: ReportViewsQueryData | undefined) => {
            if (!current?.views) {
                return { views: [nextView] };
            }

            const exists = current.views.some(
                (view) => view.eventReportViewId === nextView.eventReportViewId,
            );

            return {
                ...current,
                views: exists
                    ? current.views.map((view) =>
                          view.eventReportViewId === nextView.eventReportViewId ? nextView : view,
                      )
                    : [nextView, ...current.views],
            };
        },
    );
}

export function useReportViews(eventId: number | undefined) {
    return useQuery({
        queryKey: queryKeys.reports.views(eventId ?? 0),
        queryFn: () => fetchReportViews(eventId!),
        enabled: Boolean(eventId),
    });
}

export function useCreateReportView(eventId: number | undefined) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: CreateReportViewInput) => createReportView(eventId!, input),
        onSuccess: (createdView) => {
            if (!eventId) {
                return;
            }

            upsertReportViewInCache(queryClient, eventId, createdView);
            void queryClient.invalidateQueries({ queryKey: queryKeys.reports.views(eventId) });
        },
    });
}

export function useUpdateReportView(eventId: number | undefined) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ viewId, input }: { viewId: number; input: UpdateReportViewInput }) =>
            updateReportView(eventId!, viewId, input),
        onSuccess: (updatedView) => {
            if (!eventId) {
                return;
            }

            upsertReportViewInCache(queryClient, eventId, updatedView);
            void queryClient.invalidateQueries({ queryKey: queryKeys.reports.views(eventId) });
        },
    });
}

export function useDeleteReportView(eventId: number | undefined) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (viewId: number) => deleteReportView(eventId!, viewId),
        onSuccess: (_data, viewId) => {
            if (!eventId) {
                return;
            }

            // Drop immediately so My views / selectedView update before the refetch lands.
            queryClient.setQueryData(
                queryKeys.reports.views(eventId),
                (current: ReportViewsQueryData | undefined) => {
                    if (!current?.views) {
                        return current;
                    }

                    return {
                        ...current,
                        views: current.views.filter((view) => view.eventReportViewId !== viewId),
                    };
                },
            );
            void queryClient.invalidateQueries({ queryKey: queryKeys.reports.views(eventId) });
        },
    });
}

export function useForkReportView(eventId: number | undefined) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ viewId, input }: { viewId: number; input?: ForkReportViewInput }) =>
            forkReportView(eventId!, viewId, input),
        onSuccess: (forkedView) => {
            if (!eventId) {
                return;
            }

            upsertReportViewInCache(queryClient, eventId, forkedView);
            void queryClient.invalidateQueries({ queryKey: queryKeys.reports.views(eventId) });
        },
    });
}

export function useShareReportView(eventId: number | undefined) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            viewId,
            visibility,
        }: {
            viewId: number;
            visibility: ReportViewVisibility;
        }) => shareReportView(eventId!, viewId, visibility),
        onSuccess: () => {
            if (eventId) {
                void queryClient.invalidateQueries({ queryKey: queryKeys.reports.views(eventId) });
            }
        },
    });
}
